import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, GitBranch, Link2, Mail } from 'lucide-react';
// Code2, Cpu removed with the Tutorial Paths section below — re-add them when re-enabling it
import { projects, experience } from '../data';

const Home: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const elements = document.querySelectorAll('.stagger-child');
    elements.forEach((el, i) => {
      (el as HTMLElement).style.animation = `fadeUp 0.6s ease ${i * 0.1}s both`;
    });
  }, []);

  return (
    <div style={{ paddingTop: 64 }}>
      {/* ── Hero ─────────────────────────────────────── */}
      <section style={{ padding: '80px 24px 60px', position: 'relative', overflow: 'hidden' }}>
        {/* Background glow */}
        <div style={{
          position: 'absolute', top: -100, left: '50%', transform: 'translateX(-50%)',
          width: 600, height: 600, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(0,229,200,0.07) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ maxWidth: 760 }}>
            <div className="stagger-child" style={{ marginBottom: 20 }}>
              <span className="badge">
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--green)', display: 'inline-block', animation: 'pulse-dot 2s infinite' }} />
                Available for opportunities
              </span>
            </div>
            <h1 className="stagger-child" style={{
              fontFamily: 'var(--font-display)', fontWeight: 800,
              fontSize: 'clamp(42px, 7vw, 80px)', lineHeight: 1.05,
              letterSpacing: '-0.03em', marginBottom: 24,
            }}>
              Senior Full<br />
              <span style={{ color: 'var(--cyan)' }}>Stack</span> Dev<span style={{ color: 'var(--violet)' }}>.</span>
            </h1>
            <p className="stagger-child" style={{
              fontSize: 18, color: 'var(--text2)', lineHeight: 1.7,
              maxWidth: 560, marginBottom: 36,
            }}>
              IIT Indore → Singapore fintech → AI-powered apps. I build performant, production-grade web products with React, Golang, and a growing AI stack.
            </p>
            <div className="stagger-child" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 48 }}>
              {/* /projects route is hidden (duplicates the Featured Work section below), so this
                  scrolls to that section on the same page instead of routing to a hidden page. */}
              <a href="#featured-work" style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '12px 24px', borderRadius: 12, fontWeight: 600, fontSize: 15,
                background: 'var(--cyan)', color: '#080b10',
                transition: 'opacity 0.2s',
              }}
              onMouseOver={e => e.currentTarget.style.opacity = '0.85'}
              onMouseOut={e => e.currentTarget.style.opacity = '1'}
              >View Projects <ArrowRight size={16} /></a>

              {/* "Explore Tutorials" CTA (/react) — hidden until the roadmap has more published content, re-enable when ready to show
              <Link to="/react" style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '12px 24px', borderRadius: 12, fontWeight: 600, fontSize: 15,
                border: '1px solid var(--border2)', color: 'var(--text)',
                transition: 'border-color 0.2s',
              }}
              onMouseOver={e => e.currentTarget.style.borderColor = 'rgba(0,229,200,0.4)'}
              onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border2)'}
              >Explore Tutorials <ArrowRight size={16} /></Link>
              */}
            </div>

            {/* Social */}
            <div className="stagger-child" style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              {[
                { icon: GitBranch, href: 'https://github.com/PARMESHWARPANWAR', label: 'GitHub' },
                { icon: Link2, href: 'https://linkedin.com/in/parmeshwar777', label: 'LinkedIn' },
                { icon: Mail, href: 'mailto:parmeshwarpanwar777@gmail.com', label: 'Email' },
              ].map(({ icon: Icon, href, label }) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer"
                  style={{ color: 'var(--text2)', transition: 'color 0.2s', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14 }}
                  onMouseOver={e => e.currentTarget.style.color = 'var(--cyan)'}
                  onMouseOut={e => e.currentTarget.style.color = 'var(--text2)'}
                >
                  <Icon size={17} /> {label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats bar ────────────────────────────────── */}
      <section style={{ padding: '0 24px 60px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 1, background: 'var(--border)', borderRadius: 16, overflow: 'hidden',
            border: '1px solid var(--border)',
          }}>
            {[
              { label: 'Years Experience', value: '5+', color: 'var(--cyan)' },
              { label: 'Companies', value: '4', color: 'var(--violet)' },
              { label: 'Countries', value: '2', color: 'var(--amber)' },
              { label: 'Tech Stack', value: '20+', color: 'var(--green)' },
            ].map(stat => (
              <div key={stat.label} style={{
                padding: '28px 24px', background: 'var(--bg2)',
                display: 'flex', flexDirection: 'column', gap: 4,
              }}>
                <span style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 36, color: stat.color }}>{stat.value}</span>
                <span style={{ fontSize: 13, color: 'var(--text2)' }}>{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Featured Projects ─────────────────────────── */}
      <section id="featured-work" style={{ padding: '0 24px 80px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 36 }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 28 }}>Featured Work</h2>
            {/* "All projects" link removed — /projects route is hidden and this section already lists every project */}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
            {projects.map((p, i) => (
              <ProjectCard key={i} project={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Tutorial Paths ───────────────────────────── */}
      {/* Hidden — links to /react and /python, which are hidden until those roadmaps have more
          published content. Re-enable this section (and the TutorialCard component + Code2/Cpu
          imports below) at the same time those routes come back.
      <section style={{ padding: '0 24px 80px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ marginBottom: 12 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text3)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Learn with me
            </span>
          </div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 28, marginBottom: 36 }}>
            Tutorial Paths
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 20 }}>
            <TutorialCard
              path="/react"
              icon={<Code2 size={28} color="var(--cyan)" />}
              label="PHASE 1–6 · 49 DAYS"
              title="React Internals & Performance"
              description="Master Fiber architecture, TypeScript systems thinking, observability, dashboard engineering and SigNoz internals. Prep for senior frontend roles."
              color="var(--cyan)"
              topics={["Fiber & Reconciliation", "TypeScript Mastery", "Observability", "Dashboard Engineering", "Interview Prep"]}
            />
            <TutorialCard
              path="/python"
              icon={<Cpu size={28} color="var(--violet)" />}
              label="10 WEEKS · 47 DAYS"
              title="Python Backend & AI Engineering"
              description="From Python internals to FastAPI, RAG systems, AI agents, MongoDB, async infrastructure, and founding engineer mindset."
              color="var(--violet)"
              topics={["FastAPI & Pydantic", "LLM & Prompt Engineering", "RAG Systems", "AI Agents", "System Design"]}
            />
          </div>
        </div>
      </section>
      */}

      {/* ── Recent Experience ─────────────────────────── */}
      <section style={{ padding: '0 24px 80px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 36 }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 28 }}>Experience</h2>
            <Link to="/about" style={{ fontSize: 14, color: 'var(--cyan)', display: 'flex', alignItems: 'center', gap: 4 }}>
              Full resume <ArrowRight size={14} />
            </Link>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {experience.slice(0, 3).map((exp, i) => (
              <div key={i} style={{
                display: 'grid', gridTemplateColumns: '1fr auto',
                padding: '24px 28px', background: 'var(--bg2)',
                borderRadius: i === 0 ? '16px 16px 0 0' : i === 2 ? '0 0 16px 16px' : 0,
                border: '1px solid var(--border)',
                marginTop: i > 0 ? -1 : 0,
                transition: 'background 0.2s',
                gap: 16,
              }}
              onMouseOver={e => e.currentTarget.style.background = 'var(--bg3)'}
              onMouseOut={e => e.currentTarget.style.background = 'var(--bg2)'}
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                    <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16 }}>{exp.role}</span>
                    {i === 0 && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 100, background: 'rgba(16,185,129,0.12)', color: 'var(--green)', border: '1px solid rgba(16,185,129,0.2)', fontFamily: 'var(--font-mono)' }}>Now</span>}
                  </div>
                  <div style={{ fontSize: 14, color: 'var(--cyan)', marginBottom: 4 }}>{exp.company}</div>
                  <div style={{ fontSize: 13, color: 'var(--text2)' }}>{exp.highlights[0]}</div>
                </div>
                <div style={{ textAlign: 'right', minWidth: 120 }}>
                  <div style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text3)' }}>{exp.period}</div>
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>{exp.location}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────── */}
      <section style={{ padding: '0 24px 80px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{
            padding: '60px 48px', borderRadius: 24,
            background: 'linear-gradient(135deg, rgba(0,229,200,0.08), rgba(139,92,246,0.08))',
            border: '1px solid rgba(0,229,200,0.15)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 24,
            position: 'relative', overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute', right: -80, top: -80,
              width: 280, height: 280, borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(139,92,246,0.12), transparent 70%)',
              pointerEvents: 'none',
            }} />
            <div>
              <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 28, marginBottom: 8 }}>
                Let's build something great
              </h2>
              <p style={{ color: 'var(--text2)', fontSize: 16 }}>
                Open to senior roles, contracts & interesting problems.
              </p>
            </div>
            <a href="mailto:parmeshwarpanwar777@gmail.com" style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '14px 28px', borderRadius: 12, fontWeight: 700, fontSize: 15,
              background: 'var(--cyan)', color: '#080b10',
            }}>
              Get in touch <ArrowUpRight size={16} />
            </a>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.8); }
        }
      `}</style>
    </div>
  );
};

const ProjectCard: React.FC<{ project: any; index: number }> = ({ project }) => (
  <div style={{
    padding: '28px', borderRadius: 'var(--radius)', border: '1px solid var(--border)',
    background: 'var(--bg2)', transition: 'all 0.3s',
    display: 'flex', flexDirection: 'column', gap: 16,
  }}
  onMouseOver={e => { e.currentTarget.style.borderColor = 'rgba(0,229,200,0.25)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
  onMouseOut={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'translateY(0)'; }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <span style={{
        fontSize: 11, padding: '3px 10px', borderRadius: 100, fontFamily: 'var(--font-mono)',
        background: project.status === 'Live' ? 'rgba(16,185,129,0.1)' : 'rgba(139,92,246,0.1)',
        color: project.status === 'Live' ? 'var(--green)' : 'var(--violet)',
        border: `1px solid ${project.status === 'Live' ? 'rgba(16,185,129,0.2)' : 'rgba(139,92,246,0.2)'}`,
      }}>{project.status}</span>
      {project.link !== '#' && (
        <a href={project.link} target="_blank" rel="noopener noreferrer"
          style={{ color: 'var(--text3)', transition: 'color 0.2s' }}
          onMouseOver={e => e.currentTarget.style.color = 'var(--cyan)'}
          onMouseOut={e => e.currentTarget.style.color = 'var(--text3)'}
        ><ArrowUpRight size={18} /></a>
      )}
    </div>
    <div>
      <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 18, marginBottom: 8 }}>{project.title}</h3>
      <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.6 }}>{project.description}</p>
    </div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 'auto' }}>
      {project.tech.map((t: string) => (
        <span key={t} className="tag">{t}</span>
      ))}
    </div>
  </div>
);

// TutorialCard — only used by the Tutorial Paths section above, which is currently hidden.
// Re-enable this component at the same time as that section.
// const TutorialCard: React.FC<{ path: string; icon: React.ReactNode; label: string; title: string; description: string; color: string; topics: string[] }> = ({ path, icon, label, title, description, color, topics }) => (
//   <Link to={path} style={{
//     display: 'block', padding: '32px', borderRadius: 20,
//     border: '1px solid var(--border)', background: 'var(--bg2)',
//     transition: 'all 0.3s', textDecoration: 'none',
//   }}
//   onMouseOver={e => { e.currentTarget.style.borderColor = color + '40'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
//   onMouseOut={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'translateY(0)'; }}
//   >
//     <div style={{ marginBottom: 16 }}>{icon}</div>
//     <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text3)', letterSpacing: '0.1em', marginBottom: 10 }}>{label}</div>
//     <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 20, marginBottom: 12 }}>{title}</h3>
//     <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 20 }}>{description}</p>
//     <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20 }}>
//       {topics.map(t => (
//         <span key={t} style={{
//           fontSize: 12, padding: '3px 10px', borderRadius: 6,
//           background: 'var(--surface)', border: '1px solid var(--border2)', color: 'var(--text2)',
//         }}>{t}</span>
//       ))}
//     </div>
//     <div style={{ display: 'flex', alignItems: 'center', gap: 6, color, fontWeight: 600, fontSize: 14 }}>
//       Start Learning <ArrowRight size={14} />
//     </div>
//   </Link>
// );

export default Home;
